import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide, useTheme } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
  SlideAnimationProvider,
} from "contexts/SlideAnimation";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import {
  BentoCategory,
  NicheFollowUpQuestion,
  NicheFollowUpResponse,
  OrganizationVaProfile,
  VaOrganizationProfileStatus,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import Alert from "components/Alert";
import Loading from "components/Loading";
import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { identifyUser, trackEvent } from "utils/tracking";
import { useAlert } from "utils/useAlert";
import { useTracking } from "utils/useTracking";

import { getUserType } from "../Onboarding/AboutForm/helpers";
import SelfServeAboutPage from "./About";
import SelfServeVaBrandPreferences from "./BrandPreferences";
import SelfServeNicheFollowUpQuestionPage from "./NicheFollowUp";
import SelfServeNichePage from "./NichePick";
import SelfServeVaReview from "./ReviewAndStartSend";
import SelfServeVaTemplate from "./TemplatesPick";
import Welcome from "./Welcome";
import styles from "./styles";

const SelfServeVaOnboarding = () => {
  const {
    currentOrg,
    setCurrentUser,
    setCurrentOrg,
    organizationVaProfile,
    setUserNotifications,
    setUserIntegrations,
    setEmailSettings,
    setIncludedBentoSearchFilterIds,
    setExcludedBentoSearchFilterIds,
    setOrganizationVaProfile,
    setProfile,
  } = useContext(OrganizationUserContext);
  const theme = useTheme();
  const slideContext = useContext(SlideAnimationContext);
  const { setSubscription } = useContext(SubscriptionContext);
  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);
  const { slideProps } = useContext(SlideAnimationContext);
  const [message, severity, setAlert, closeAlert] = useAlert();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { trackVaStep } = useTracking();
  const [fetchUserLoading, setFetchUserLoading] = useState(true);

  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);
  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [followupNicheQuestions, setFollowupNicheQuestions] = useState<
    NicheFollowUpQuestion[]
  >([]);
  const [followUpNicheResponses, setFollowUpNicheResponses] = useState<
    NicheFollowUpResponse[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<BentoCategory[]>(
    [],
  );
  const [nichesFetched, setNichesFetched] = useState<boolean>(false);

  const needsFollowUps =
    selectedCategories?.length > 0
      ? followupNicheQuestions?.some((question) =>
          selectedCategories
            .map((cat) => cat.id)
            .includes(question.triggerBentoCategoryId),
        ) || followUpNicheResponses?.length > 0
      : false;

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      setFetchUserLoading(false);
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
      setUserIntegrations(res.userIntegrations);
      setUserNotifications(res.userNotifications);
      setEmailSettings(res.emailSetting);
      setOnboardingForm((prev) => ({
        ...prev,
        ...res.organizationProfile,
        name: res.organizationUser.name,
        userType: getUserType(res.organizationProfile),
      }));
      setIncludedBentoSearchFilterIds(res.includedBentoSearchFilterIds);
      setExcludedBentoSearchFilterIds(res.excludedBentoSearchFilterIds);
      setOrganizationVaProfile((prev) => {
        if (prev && res?.organizationVaProfile) {
          prev.status = res.organizationVaProfile?.status;
          prev.id = res.organizationVaProfile?.id;
          prev.personalizeOutreach =
            res.organizationVaProfile?.personalizeOutreach;
          return prev;
        } else {
          return res.organizationVaProfile;
        }
      });
      setProfile(res.organizationProfile);
      redirectToRightPage(res.organizationVaProfile);
      slideContext.slideInUp();
    } catch (error) {}
  };

  const fetchNiches = async () => {
    const { niches } = await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/organization-niches`,
    );
    setOrganizationVaProfile((prev) => {
      if (prev) {
        prev.niches = niches;
      }
      return prev;
    });
    const responses = [];
    if (niches?.length > 0) {
      for (const n of niches) {
        for (const r of n.followUpResponses) {
          if (r.nicheFollowUpQuestionId) {
            responses.push(r);
          }
        }
      }
    }
    setFollowUpNicheResponses(responses);
    setNichesFetched(true);
  };

  const getCategoriesToPickFrom = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding?for_va=${true}&exclude_category_names=travel`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong. Please refresh and retry again",
        "error",
      );
    }
  };

  const fetchFollowUpQuestions = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/niche-follow-up-questions`,
      );
      setFollowupNicheQuestions(res.nicheFollowUpQuestions);
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong. Please refresh and retry again",
        "error",
      );
    }
  };

  const redirectToRightPage = (vaProfile: OrganizationVaProfile) => {
    if (vaProfile?.status === VaOrganizationProfileStatus.completed) {
      navigate(`/${routes.virtualAssistant}`);
      return;
    }

    trackVaStep(routes.welcomeSelfServeOnboarding);
    navigate(`/${routes.welcomeSelfServeOnboarding}`);
  };

  useEffect(() => {
    fetchUserInfo();
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentOrg?.id) {
      getCategoriesToPickFrom();
      fetchOutreachTemplates();
      fetchFollowUpQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (organizationVaProfile?.id && currentOrg?.id) {
      fetchNiches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationVaProfile?.id, currentOrg?.id]);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <>
      <Grid container sx={styles.root}>
        <Slide
          in={slideProps.in}
          direction={slideProps.direction}
          easing={{
            enter: theme.transitions.easing.easeInOut,
            exit: theme.transitions.easing.easeIn,
          }}
          timeout={{
            enter: ENTER_DELAY,
            exit: EXIT_DELAY,
          }}
        >
          <Grid
            sx={[
              styles.formContainer,
              {
                maxWidth: {
                  md: isPage(routes.templatesSelfServeOnboarding)
                    ? "2400px"
                    : "1200px",
                },
              },
            ]}
            item
            xs={12}
          >
            <Routes>
              <Route
                path="*"
                element={
                  <Navigate
                    to={`/${routes.welcomeSelfServeOnboarding}`}
                    replace
                  />
                }
              />
              <Route path="/welcome" element={<Welcome />} />
              <Route
                path="/about"
                element={
                  <SelfServeAboutPage
                    onboardingForm={onboardingForm}
                    setOnboardingForm={setOnboardingForm}
                    nichesFetched={nichesFetched}
                  />
                }
              />
              <Route
                path="/niches"
                element={
                  <SelfServeNichePage
                    categoriesToPickFrom={categoriesToPickFrom}
                    needsFollowUps={needsFollowUps}
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                    followupQuestions={followupNicheQuestions}
                  />
                }
              />
              <Route
                path="/niches-follow-up"
                element={
                  <SelfServeNicheFollowUpQuestionPage
                    nicheFollowUpQuestions={followupNicheQuestions}
                    nicheFollowUpResponses={followUpNicheResponses}
                    setFollowUpNicheResponses={setFollowUpNicheResponses}
                  />
                }
              />
              <Route
                path="/templates"
                element={
                  <SelfServeVaTemplate needsFollowUps={needsFollowUps} />
                }
              />
              <Route
                path="/preferences"
                element={<SelfServeVaBrandPreferences />}
              />
              <Route path="/review" element={<SelfServeVaReview />} />
            </Routes>
          </Grid>
        </Slide>
      </Grid>

      <Alert message={message} severity={severity} closeAlert={closeAlert} />
    </>
  );
};

export default function VAOnboarding() {
  return (
    <SlideAnimationProvider>
      <SelfServeVaOnboarding />
    </SlideAnimationProvider>
  );
}
